import React, { useState } from "react";
import { MotifMenu, MotifMenuItem, MotifIcon, MotifButton  } from "@ey-xd/motif-react";
import { FailedUploadsList } from "./FailedUploadsList";
import { deleteIcon, infoIcon, IconoirCloudSync, closeIcon } from "../../common/MotifIcons";
import { apiQueryWithToken } from "../../common/services/apiBaseUrlConfig";
import { getDateTimeFormat } from "../../common/utility/utility";

import "./FailedFilesUpload.scss";
import logger from "../../common/utils/logger";

export const FailedFilesUpload = ({ show, onClose, rowData, failedUploads, setFailedUploads, failedUploadData, setFailedUploadLoadData, isFailedUploadLoadData, onDownloadArcFilesClick, isRetryDownloadDisabled }) => {
  // const [failedUploads, setFailedUploads] = useState(data);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const handleToggleSelect = (fileId) => {
    setSelectedFiles((prev) =>
      prev.includes(fileId)
        ? prev.filter((id) => id !== fileId)
        : [...prev, fileId]
    );
  };

  const handleToggleSelectAll = () => {
    if (selectedFiles.length === failedUploads.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(failedUploads.map((file) => file.id));
    }
  };

  const handleReupload = (id) => {
    const updated = failedUploads
      .filter((file) => {
        if (file.id === id) {
          return file.attempts < 2; // Keep only if attempts < 2 (0-based, so 3rd = remove)
        }
        return true;
      })
      .map((file) => {
        if (file.id === id) {
          return {
            ...file,
            attempts: file.attempts + 1,
          };
        }
        return file;
      });

    setFailedUploads(updated);
    setSelectedFiles((prev) => prev.filter((fileId) => fileId !== id));
    logger.info(`Reupload attempted for file ID: ${id}`);
  };

  const handleReuploadAll = () => {
    if (selectedFiles.length === 0) return;

    const updated = failedUploads
      .filter((file) => {
        if (selectedFiles.includes(file.id)) {
          return file.attempts < 2;
        }
        return true;
      })
      .map((file) => {
        if (selectedFiles.includes(file.id)) {
          return {
            ...file,
            attempts: file.attempts + 1,
          };
        }
        return file;
      });

    setFailedUploads(updated);
    setSelectedFiles([]);
    logger.info("Bulk reupload done for file IDs:", selectedFiles);
  };

  const handleDelete = (id) => {
    if (id === "all") {
      setFailedUploads((prev) =>
        prev.filter((file) => !selectedFiles.includes(file.id))
      );
      setSelectedFiles([]);
    } else {
      setFailedUploads((prev) => prev.filter((file) => file.id !== id));
      setSelectedFiles((prev) => prev.filter((fileId) => fileId !== id));
    }
  };

  // ARC "Retry Download" both re-triggers the ARC archive download and clears the previous
  // attempt's failure records (same api/errors/delete used on the Failed Upload page) so the
  // stale failed entries are removed once the retry is kicked off.
  const handleRetryDownload = async () => {
    onDownloadArcFilesClick?.(rowData);
    // Close the panel as soon as the retry is kicked off; the error-log cleanup
    // below still runs to completion in the background.
    onClose?.();

    // ARC retry clears the previous attempt's failure records for the whole
    // engagement (isArcDownload) keyed by the uploader's email, instead of
    // deleting by individual errorIds/transactions.
    const engagementIdGUID =
      rowData?.engagementId_GUID || failedUploadData?.[0]?.engagementIdGUID;
    if (!engagementIdGUID) return;

    try {
      await apiQueryWithToken.delete("api/errors/delete", {
        headers: { "Content-Type": "application/json" },
        data: {
          ErrorIds: [],
          EngagementIdGUID: engagementIdGUID,
          TransactionID: "",
          emailAddress: failedUploadData?.[0]?.uploadedBy,
          isArcDownload: true,
          arcDownloadEngagementGuids: [engagementIdGUID],
        },
      });
    } catch (error) {
      logger.error("Failed to delete ARC error logs on retry download:", error);
    }
  };

  // ARC engagements present a read-only failure list with a single "Retry Download"
  // action instead of the per-file re-upload/delete flow used for other sources.
  const isArcSource = String(rowData?.dataSource || "").toUpperCase() === "ARC";

  if (isArcSource) {
    const arcFailedFiles = failedUploadData || [];
    return (
      <MotifMenu show={show} onClose={onClose} className="motif-menu-container arc-failed-menu">
        <div className="arc-failed-header">
          <div className="arc-failed-header-main">
            <div className="arc-failed-title-row">
              <span className="arc-failed-title">Failed Downloads</span>
              <span className="arc-failed-count">{arcFailedFiles.length}</span>
            </div>
            <div className="arc-failed-engagement">
              {rowData?.engagementId} | {rowData?.engagementName}
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            className="arc-failed-close"
            onClick={onClose}
          >
            <MotifIcon src={closeIcon} />
          </button>
        </div>

        <div className="arc-failed-content">
          <div className="arc-failed-section-label">
            Client Data & EY-Owned Data
          </div>
          <div className="arc-failed-banner" role="status">
            <MotifIcon src={infoIcon} className="arc-failed-banner-icon" />
            <p className="arc-failed-banner-text">
              Some files could not be downloaded successfully. Please click
              "Retry Download" to fetch the failed files.
            </p>
          </div>

          <div className="arc-failed-retry-row">
            <MotifButton
              variant="secondary"
              size="small"
              className="arc-failed-retry-btn"
              disabled={isRetryDownloadDisabled}
              onClick={handleRetryDownload}
            >
              <MotifIcon src={IconoirCloudSync} className="arc-failed-retry-icon" />
              Retry download
            </MotifButton>
          </div>

          <div className="arc-failed-table">
            <div className="arc-failed-table-head">
              <span className="arc-failed-col-name">File Name</span>
              <span className="arc-failed-col-reason">Reason for Failure</span>
            </div>
            {arcFailedFiles.length === 0 ? (
              <p className="arc-failed-empty">No failed files.</p>
            ) : (
              arcFailedFiles.map((file, idx) => {
                const fileName = file.uploadFileName || file.fileName || "";
                const reasonText =
                  file.customErrorMessage ||
                  file.message ||
                  file.reasonPhrase ||
                  file.reason ||
                  "Unknown error";
                const uploadedDate = file.createdDate
                  ? getDateTimeFormat(file.createdDate)
                  : "";
                return (
                  <div
                    className="arc-failed-row"
                    key={file.id || file.errorId || idx}
                  >
                    <div className="arc-failed-col-name">
                      <span className="arc-failed-file-name" title={fileName}>
                        {fileName}
                      </span>
                      {uploadedDate && (
                        <span className="arc-failed-file-date">{uploadedDate}</span>
                      )}
                    </div>
                    <span className="arc-failed-col-reason" title={reasonText}>
                      {reasonText}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="close-button-container arc-failed-footer">
          <MotifButton
            onClick={onClose}
            variant="primary"
            className="close-button"
          >
            Close
          </MotifButton>
        </div>
      </MotifMenu>
    );
  }

  return (
    <MotifMenu show={show} onClose={onClose} className="motif-menu-container">
      <div className="motif-menu-view-files-container">
        <MotifMenuItem className="motif-menu-view-files" disabled>
          <strong>
            Failed Upload{" "}
            <span className="no-of-files">{failedUploads.length}</span>
          </strong>
          <div className="engagement-id addFilesMenu">
            {rowData?.engagementId} | {rowData?.engagementName}
          </div>
        </MotifMenuItem>
      </div>

      <div className="tab-buttons" style={{ margin: "0" }}>
        <div
          className="motif-menu-view-files-container"
          style={{ padding: "10px 10px 0 10px" }}
        >
          <MotifMenuItem className="motif-menu-view-files" disabled>
            <strong>Client Data & EY Owned Data</strong>
          </MotifMenuItem>
        </div>
      </div>
      {failedUploads.length > 0 && (
        <div className="header-input-partner">
          <MotifButton
            style={{ gap: "6px" }}
            variant="secondary"
            onClick={handleReuploadAll}
            disabled={selectedFiles.length === 0}
          >
            <MotifIcon
              src="data:image/svg+xml;base64,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"
              style={{ width: "17px", height: "17px" }}
            />
            Re-upload all
          </MotifButton>
          <MotifButton
            style={{ gap: "6px" }}
            variant="secondary"
            onClick={() => handleDelete("all")}
            disabled={selectedFiles.length === 0}
          >
            <MotifIcon
              src={deleteIcon}
              style={{ width: "17px", height: "17px", gap: "16px" }}
            />
            Delete all
          </MotifButton>
        </div>
      )}

      <div className="tab-buttons" style={{ margin: "0", padding: "0px 10px" }}>
        <FailedUploadsList
        failedUploadData={failedUploadData || []}
          setFailedUploadLoadData={setFailedUploadLoadData}
          isFailedUploadLoadData={isFailedUploadLoadData}
          failedUploads={failedUploads}
          selectedFileIds={selectedFiles}
          onToggleSelect={handleToggleSelect}
          onToggleSelectAll={handleToggleSelectAll}
          onDelete={handleDelete}
          onReupload={handleReupload}
        />
      </div>
      {failedUploads.length > 0 && (
      <p className="upload-message">
        You can reupload the files using the 'Add Files' option.
      </p>)}

      <div
        className={`close-button-container ${
          failedUploads.length === 0 ? "fixed-bottom-right" : ""
        }`}
      >
        <MotifButton
          onClick={onClose}
          variant="primary"
          className="close-button"
        >
          Close
        </MotifButton>
      </div>
    </MotifMenu>
  );
};
